import { Periodicity } from '@components/time-period-selector/PeriodsTypes';
import { FilterParam } from '@services/FiltersTypes';
import { DataType, FeatureOption, FilterKeys, Filters, FilterTitle, PageName } from '@utils/pageConfig/Types';
import { NO_OPTIONS } from '@utils/StringConstants';
import { Dispatch, SetStateAction } from 'react';

export const EMPTY_FEATURE_OPTION = {
  dataType: '',
  feature: '',
  filterKey: -1,
  label: NO_OPTIONS,
  value: NO_OPTIONS,
};

export const SELECT_FEATURE_OPTION = {
  dataType: FilterTitle.SELECT,
  label: FilterTitle.SELECT,
  feature: FilterTitle.SELECT,
  value: FilterTitle.SELECT,
  filterKey: FilterKeys.SELECT,
};

export const CATEGORY_FEATURE_OPTION = {
  dataType: DataType.CATEGORY,
  label: FilterTitle.CATEGORY,
  feature: FilterTitle.CATEGORY,
  value: FilterTitle.CATEGORY,
  filterKey: FilterKeys.CATEGORY,
};

export const isDataTypeFeature = (featureOption: FeatureOption) => {
  return featureOption.filterKey === FilterKeys.ADDFEAT || featureOption.filterKey === FilterKeys.SEGFEAT;
};

export const constructFeature = (feat: Filters): FeatureOption => {
  return {
    dataType: feat.dataType,
    feature: feat.feature,
    label: feat.filterTitle,
    value: feat.filterTitle,
    filterKey: feat.filterKey,
  };
};

/*
 * Build options for selector.

 * Displays filterTitle field for better viewing.
 * Removes feature === null as it will serve no effect after filtering
 *
 */
export const buildOptions = (filters: Filters[]): FeatureOption[] => {
  return filters
    .filter((f) => f.feature !== null)
    .map((f) => {
      return {
        dataType: f.dataType,
        feature: f.feature,
        value: f.filterTitle,
        label: f.filterTitle,
        filterKey: f.filterKey,
      };
    });
};

export const getAndUpdateFeatureSelector = (
  allFeatureValues: Filters[],
  feature: FeatureOption,
  setFeature: Dispatch<SetStateAction<FeatureOption>>,
) => {
  const featureOption = getFeatureSelector(allFeatureValues, feature);

  if (featureOption.dataType !== '') {
    setFeature(featureOption);
  }
};

export const getFeatureSelector = (
  allFeatureValues: Filters[],
  feature: FeatureOption,
  disabledFeatureValues: FilterKeys[] = [],
  pageName?: PageName,
) => {
  if (!allFeatureValues) {
    return feature;
  }
  // passed link segments specific
  const filteredAllFeatureValues = allFeatureValues.filter((f) => !disabledFeatureValues.includes(f.filterKey));
  const hasAvailableOptions = filteredAllFeatureValues.some((feat) => !!feat.feature);
  if (!hasAvailableOptions) {
    if (pageName === PageName.TRADING_ASSISTANT) {
      //special handling for TA page due to segment by dropdown need to reset to SELECT
      //instead of empty feature option
      return SELECT_FEATURE_OPTION;
    } else if (pageName && [PageName.EXPLORE, PageName.OPPORTUNITY_REVIEW].includes(pageName)) {
      return CATEGORY_FEATURE_OPTION;
    }
    return EMPTY_FEATURE_OPTION;
  }

  const currentFeature = filteredAllFeatureValues.find((feat) => feat.feature === feature.feature);
  if (currentFeature) {
    // Always set to previous selected feature if match when product group is changed
    return constructFeature(currentFeature);
  }

  if (isDataTypeFeature(feature)) {
    return checkAvailableSegFeatAddFeatOption(filteredAllFeatureValues, pageName);
  }

  return checkAvailableFeatureOption(filteredAllFeatureValues, pageName);
};

const checkAvailableSegFeatAddFeatOption = (allFeatureValues: Filters[], pageName: PageName | undefined) => {
  const segFeat = allFeatureValues.find((feat) => feat.filterKey === FilterKeys.SEGFEAT);
  const addFeat = allFeatureValues.find((feat) => feat.filterKey === FilterKeys.ADDFEAT);

  // Select the first feature (SEGFEAT) when product group is changed
  if (segFeat) {
    return constructFeature(segFeat);
  }

  // Select the first feature (ADDFEAT) when product group is changed
  if (addFeat) {
    return constructFeature(addFeat);
  }

  // Default to BRAND or other available options when ADDFEAT and SEGFEAT is not available
  return checkAvailableFeatureOption(allFeatureValues, pageName);
};

const checkAvailableFeatureOption = (allFeatureValues: Filters[], pageName: PageName | undefined) => {
  const allOptions = buildOptions(allFeatureValues);
  const brandsAvailable = allOptions.find((feat) => feat.feature === 'Brands');
  const categoryAvailable = allOptions.find((feat) => feat.feature === 'Category');

  if (pageName && [PageName.EXPLORE, PageName.OPPORTUNITY_REVIEW].includes(pageName) && categoryAvailable) {
    return CATEGORY_FEATURE_OPTION;
  }

  if (brandsAvailable) {
    return brandsAvailable;
  }

  // Default to next available option if no brands
  return allOptions[0];
};

/**
 * @param featureValueParam Object to be combined with periodicity
 * @param periodicity Monthly, Weekly
 * @returns Object to be queried for feature-value API
 */
export const constructFeatureValueParam = (featureValueParam: FilterParam, periodicity: Periodicity) => {
  return {
    productType: featureValueParam.productType,
    selectedProductGroup: featureValueParam.selectedProductGroup,
    initialFilters: featureValueParam.initialFilters,
    selectedCountries: featureValueParam.selectedCountries,
    pagesConfig: featureValueParam.pagesConfig,
    responseConfig: featureValueParam.responseConfig,
    allFilters: featureValueParam.allFilters,
    timeFrame: periodicity,
    subscriptions: featureValueParam.subscriptions,
    includeCommonFeaturesOnly: featureValueParam.includeCommonFeaturesOnly,
  };
};
